import { useTranslation } from 'react-i18next';
import type { VendorKpiCategory } from '@/types/vendor-kpi.ts';
import { getScoreColor, getCardTier } from '@/types/vendor-kpi.ts';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface VendorCardProps {
  name: string;
  initials: string;
  globalScore: number;
  categoryBadge?: string;
  categoryColor?: string;
  categories: VendorKpiCategory[];
  totalSimulations?: number;
  lastSession?: string;
  onClick?: () => void;
}

export default function VendorCard({
  name,
  initials,
  globalScore,
  categoryBadge,
  categoryColor,
  categories,
  totalSimulations,
  lastSession,
  onClick,
}: VendorCardProps) {
  const { t } = useTranslation();
  const tier = getCardTier(globalScore);
  const scoreColor = getScoreColor(globalScore);

  const borderClass =
    tier === 'elite'
      ? 'border-transparent'
      : tier === 'pro'
        ? 'border-primary/40'
        : 'border-border';

  const wrapperClass =
    tier === 'elite'
      ? 'p-[1px] rounded-2xl bg-gradient-to-br from-primary via-[#C9A84C] to-primary card-glow'
      : '';

  const card = (
    <button
      onClick={onClick}
      className={`w-full rounded-2xl ${tier !== 'elite' ? `border ${borderClass}` : ''} bg-card p-4 text-left transition-all hover:shadow-lg hover:shadow-primary/10 hover:-translate-y-0.5`}
    >
      {/* Top: Score + Badge */}
      <div className="mb-3 flex items-center justify-between">
        <span className="text-3xl font-extrabold" style={{ color: scoreColor }}>
          {globalScore}
        </span>
        {categoryBadge && (
          <Badge
            variant="outline"
            className="rounded-full text-[10px] font-bold"
            style={{
              backgroundColor: `${categoryColor ?? 'var(--primary)'}20`,
              color: categoryColor ?? 'var(--primary)',
              borderColor: 'transparent',
            }}
          >
            {categoryBadge}
          </Badge>
        )}
      </div>

      {/* Avatar */}
      <div className="mb-3 flex justify-center">
        <Avatar className="size-14">
          <AvatarFallback
            className="text-lg font-bold text-white"
            style={{ backgroundColor: categoryColor ?? 'var(--primary)' }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Name */}
      <p className="mb-3 text-center text-sm font-bold uppercase tracking-wide text-foreground">
        {name}
      </p>

      {/* 6 stats in 2x3 grid with mini bars */}
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-1.5">
            <span className="text-[10px] font-bold" style={{ color: getScoreColor(cat.score) }}>
              {cat.score}
            </span>
            <span className="text-[10px] text-muted-foreground">{cat.shortName}</span>
            <div className="h-1 flex-1 overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(cat.score / 99) * 100}%`,
                  backgroundColor: getScoreColor(cat.score),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {(totalSimulations !== undefined || lastSession) && (
        <div className="mt-3 flex items-center justify-between border-t border-border pt-2 text-[10px] text-muted-foreground">
          {totalSimulations !== undefined && <span>{totalSimulations} {t('common.sim')}</span>}
          {lastSession && <span>{lastSession}</span>}
        </div>
      )}
    </button>
  );

  if (tier === 'elite') {
    return <div className={wrapperClass}>{card}</div>;
  }

  return card;
}
